// export default CountriesComponent;

import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Paper,
} from "@mui/material";
import {
  fetchCompanyLocations,
  setSelectedMonth,
} from "../redux/slices/companyLocationsSlice";

const CountriesComponent = ({ sidebarOpen = true }) => {
  const dispatch = useDispatch();
  const { locations, loading, error, selectedMonth } = useSelector(
    (state) => state.companyLocations
  );

  useEffect(() => {
    dispatch(fetchCompanyLocations());
  }, [dispatch]);

  const handleMonthChange = (event) => {
    dispatch(setSelectedMonth(event.target.value));
  };

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 212 : 120;

  if (loading) {
    return (
      <Box
        sx={{
          padding: 2,
          ml: {
            xs: "0px",
            sm: "0px",
            md: `${sidebarWidth}px`,
            xl: `${sidebarWidth}px`,
          },
          transition: "margin-left 0.3s ease",
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          padding: 2,
          ml: {
            xs: "0px",
            sm: "0px",
            md: `${sidebarWidth}px`,
            xl: `${sidebarWidth}px`,
          },
          transition: "margin-left 0.3s ease",
        }}
      >
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        padding: { xs: 1, sm: 1.5, lg: 2.5 },
        fontFamily: "'Segoe UI', 'Roboto', sans-serif",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        marginRight: { xs: 0, sm: 0, lg: 3.75 }, // 30px converted to theme units
        transition: "margin-left 0.3s ease",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", lg: "row" },
          justifyContent: {
            xs: "flex-start",
            sm: "flex-start",
            lg: "space-between",
          },
          alignItems: { xs: "flex-start", sm: "flex-start", lg: "center" },
          gap: { xs: 2, sm: 2, lg: 0 },
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5, lg: 1.5 },
            flexWrap: { xs: "wrap", sm: "wrap", lg: "nowrap" },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: 15, sm: 15, lg: 18 },
              fontWeight: 700,
              color: "#2B3674",
              m: 0,
              fontFamily: "Outfit",
            }}
          >
            Top countries
          </Typography>
        </Box>

        {/* <FormControl size="small" variant="standard">
          <Select
            value={selectedMonth}
            onChange={handleMonthChange}
            disableUnderline
            sx={{
              fontSize: "17.18px",
              color: "#A3AED0",
              "& .MuiSelect-select": {
                padding: "2px 4px",
                fontSize: 11,
                fontFamily: "Outfit",
                fontWeight: 400,
              },
            }}
          >
            <MenuItem value="January">Month: January</MenuItem>
            <MenuItem value="February">Month: February</MenuItem>
            <MenuItem value="March">Month: March</MenuItem>
          </Select>
        </FormControl> */}
      </Box>

      {/* MUI Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          overflowX: { xs: "auto", sm: "auto", lg: "visible" },
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: 3,
          },
        }}
      >
        <Table
          sx={{
            minWidth: { xs: 800, sm: 800, lg: 650 },
            "& .MuiTableCell-root": {
              borderBottom: "1px solid #f0f0f0",
              padding: { xs: "8px 8px", sm: "10px 12px", lg: "12px 16px" },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 120, sm: 140, lg: "auto" },
                }}
              >
                Countries
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 100, sm: 120, lg: "auto" },
                }}
              >
                Companies Registered
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 80, sm: 100, lg: "auto" },
                }}
              >
                Users
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 80, sm: 100, lg: "auto" },
                }}
              >
                Free trial
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 90, sm: 110, lg: "auto" },
                }}
              >
                Standard plan
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 90, sm: 110, lg: "auto" },
                }}
              >
                Premium plan
              </TableCell>
              <TableCell
                sx={{
                  fontSize: { xs: "14.05px", sm: "14.05px", lg: "15.05px" },
                  fontWeight: 500,
                  color: "#A3AED0",
                  fontFamily: "Outfit",
                  padding: { xs: "6px 8px", sm: "8px 12px", lg: "8px 16px" },
                  minWidth: { xs: 80, sm: 100, lg: "auto" },
                }}
              >
                Revenue
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {locations.map((location, index) => (
              <TableRow
                key={index}
                hover
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f9f9f9",
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#333",
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 120, sm: 140, lg: "auto" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "12px",
                        sm: "12px",
                        lg: "15px",
                      },
                      fontWeight: 700,
                      color: "#2B3674",
                      fontFamily: "Outfit",
                    }}
                  >
                    {location.location}
                  </Typography>
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#A3AED0",
                    fontFamily: "Outfit",
                    fontWeight: 400,
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 100, sm: 120, lg: "auto" },
                  }}
                >
                  {location.companiesCount}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#333",
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 80, sm: 100, lg: "auto" },
                  }}
                >
                  {location.recruitersCount}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#A3AED0",
                    fontFamily: "Outfit",
                    fontWeight: 400,
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 80, sm: 100, lg: "auto" },
                  }}
                >
                  0%
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#A3AED0",
                    fontFamily: "Outfit",
                    fontWeight: 400,
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 90, sm: 110, lg: "auto" },
                  }}
                >
                  0%
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#A3AED0",
                    fontFamily: "Outfit",
                    fontWeight: 400,
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 90, sm: 110, lg: "auto" },
                  }}
                >
                  0%
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: 11, sm: 12, lg: 13 },
                    color: "#9048FB",
                    fontFamily: "Outfit",
                    fontWeight: 600,
                    borderBottom: "1px solid #f8f8f8",
                    padding: {
                      xs: "8px 8px",
                      sm: "10px 12px",
                      lg: "12px 16px",
                    },
                    minWidth: { xs: 80, sm: 100, lg: "auto" },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#7c3aed",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    $0
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CountriesComponent;
